import type { OwnerBookingWithTypeDto } from '@shared/core/bookings'
import { ownerEmailAnswers } from '@shared/core/bookings'
import classNames from 'classnames'
import { useState } from 'react'
import generateRandomId from 'src/helpers/generate-random-id'
import { FormattedText } from 'src/modules/core/components/formatted-text'
import { Typography } from 'src/modules/core/components/typography'
import { useBookingByEventId } from '../../hooks/use-booking-by-event-id'
import { BookingAnswersSection } from '../booking-answers-section'
import { buildBookingDetailsFields } from './booking-details-section.domain'
import type { BookingDetailsBodyProps, BookingDetailsSectionProps } from './booking-details-section.interface'
import classes from './booking-details-section.module.scss'

export function BookingDetailsSection({ eventId, className }: BookingDetailsSectionProps) {
  const [titleId] = useState(generateRandomId)
  const { data: booking, isLoading, isError } = useBookingByEventId(eventId, { enabled: true })

  return (
    <section className={classNames(classes.root, className)} aria-labelledby={titleId} data-testid="booking-details">
      <Typography id={titleId} variant="h4" className={classes.title}>
        <FormattedText id="event.bookingDetails.title" />
      </Typography>
      <BookingDetailsBody booking={booking} isLoading={isLoading} isError={isError} />
    </section>
  )
}

function BookingDetailsBody({ booking, isLoading, isError }: BookingDetailsBodyProps) {
  if (isLoading) return <BookingDetailsStatus messageId="event.bookingDetails.loading" />
  if (isError) return <BookingDetailsStatus messageId="event.bookingDetails.loadError" />
  if (!booking) return <BookingDetailsStatus messageId="event.bookingDetails.notAvailable" />
  return <BookingDetailsContent booking={booking} />
}

function BookingDetailsStatus({ messageId }: { messageId: string }) {
  return (
    <Typography variant="body" className={classes.status}>
      <FormattedText id={messageId} />
    </Typography>
  )
}

function BookingDetailsContent({ booking }: { booking: OwnerBookingWithTypeDto }) {
  return (
    <>
      <dl className={classes.fields}>
        {buildBookingDetailsFields(booking).map(({ messageId, value }) => (
          <div key={messageId} className={classes.field}>
            <dt className={classes.label}>
              <FormattedText id={messageId} />
            </dt>
            <dd className={classes.value}>{value}</dd>
          </div>
        ))}
      </dl>
      <BookingAnswersSection
        className={classes.answers}
        title={<FormattedText id="event.bookingDetails.answersTitle" />}
        lines={ownerEmailAnswers(booking)}
      />
    </>
  )
}
